// blockRangeToDocPositions.js
// Convert a character range within a specific block (by data-block-id) to
// absolute ProseMirror document positions.
//
// This is used for applying marks to offsets coming from external systems
// (e.g., AI tasks) that refer to a block and a [start,end] text range.

/**
 * @typedef {{from: number, to: number}} DocRange
 */

function normalizeWhitespace(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
}

function escapeRegex(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * @param {import('prosemirror-model').Node} doc
 * @param {string} blockId
 * @returns {{node: import('prosemirror-model').Node, pos: number} | null}
 */
export function findBlockInDoc(doc, blockId) {
    if (!doc || !blockId) return null;

    let result = null;
    doc.descendants((node, pos) => {
        if (result) return false;
        if (node.isBlock && node.attrs?.blockId === blockId) {
            result = {node, pos};
            return false;
        }
        return undefined;
    });
    return result;
}

/**
 * Map plain-text offsets inside a block to absolute doc positions (no DOM).
 *
 * @param {import('prosemirror-model').Node} doc
 * @param {string} blockId
 * @param {number} start
 * @param {number} end
 * @returns {DocRange | null}
 */
export function blockTextOffsetsToDocPositions(doc, blockId, start, end) {
    const block = findBlockInDoc(doc, blockId);
    if (!block) return null;

    const text = block.node.textContent;
    const clampedStart = Math.max(0, Math.min(start ?? 0, text.length));
    const clampedEnd = Math.max(0, Math.min(end ?? 0, text.length));
    if (clampedStart >= clampedEnd) return null;

    const contentStart = block.pos + 1;
    let textOffset = 0;
    let from = null;
    let to = null;

    block.node.descendants((node, relPos) => {
        if (!node.isText) return;

        const len = node.text.length;
        const nodeTextStart = textOffset;
        const nodeTextEnd = textOffset + len;

        if (from == null && clampedStart >= nodeTextStart && clampedStart <= nodeTextEnd) {
            from = contentStart + relPos + (clampedStart - nodeTextStart);
        }
        if (to == null && clampedEnd >= nodeTextStart && clampedEnd <= nodeTextEnd) {
            to = contentStart + relPos + (clampedEnd - nodeTextStart);
        }

        textOffset += len;
    });

    if (from == null || to == null || from >= to) return null;

    return {from, to};
}

/**
 * Find [start, end) character offsets for snippet text inside a block.
 *
 * @param {import('prosemirror-model').Node} doc
 * @param {string} blockId
 * @param {string} snippet
 * @returns {[number, number] | null}
 */
export function findSnippetOffsetsInBlock(doc, blockId, snippet) {
    const block = findBlockInDoc(doc, blockId);
    if (!block || !snippet) return null;

    const text = block.node.textContent;
    const direct = text.indexOf(snippet);
    if (direct !== -1) {
        return [direct, direct + snippet.length];
    }

    const normalizedSnippet = normalizeWhitespace(snippet);
    const normalizedText = normalizeWhitespace(text);
    const normalizedIndex = normalizedText.indexOf(normalizedSnippet);
    if (normalizedIndex !== -1 && normalizedSnippet.length > 0) {
        const pattern = normalizedSnippet.split(/\s+/).map(escapeRegex).join('\\s+');
        const match = new RegExp(pattern).exec(text);
        if (match) {
            return [match.index, match.index + match[0].length];
        }
    }

    const flexiblePattern = escapeRegex(snippet.trim()).replace(/\s+/g, '\\s+');
    const flexibleMatch = new RegExp(flexiblePattern, 'i').exec(text);
    if (flexibleMatch) {
        return [flexibleMatch.index, flexibleMatch.index + flexibleMatch[0].length];
    }

    return null;
}

/**
 * Resolve a statement/task range to doc positions.
 * Falls back to matching statement text when API range is missing or [0,0].
 *
 * @param {import('prosemirror-view').EditorView} view
 * @param {string} blockId
 * @param {number[]|null|undefined} range
 * @param {string|null|undefined} text
 * @returns {DocRange | null}
 */
export function resolveStatementDocRange(view, blockId, range, text) {
    if (!view) return null;
    if (!blockId && !text) return null;

    const start = Array.isArray(range) ? range[0] : null;
    const end = Array.isArray(range) ? range[1] : null;

    if (Number.isFinite(start) && Number.isFinite(end) && start < end) {
        const domRange = blockRangeToDocPositions(view, blockId, start, end);
        if (domRange) return domRange;

        const docRange = blockTextOffsetsToDocPositions(view.state.doc, blockId, start, end);
        if (docRange) return docRange;
    }

    if (text && blockId) {
        const offsets = findSnippetOffsetsInBlock(view.state.doc, blockId, text);
        if (offsets) {
            return blockTextOffsetsToDocPositions(view.state.doc, blockId, offsets[0], offsets[1]);
        }
    }

    return null;
}

/**
 * Walk all text nodes under `root` and map the given character offsets into
 * concrete DOM boundary points.
 */
function findDomTextBoundaries(root, start, end) {
    /** @type {Array<Text>} */
    const textNodes = [];

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
            if (!node.nodeValue || node.nodeValue.length === 0) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
        }
    });

    let n = walker.nextNode();
    while (n) {
        textNodes.push(/** @type {Text} */ (n));
        n = walker.nextNode();
    }

    const total = textNodes.reduce((acc, t) => acc + t.nodeValue.length, 0);

    const clampedStart = Math.max(0, Math.min(start ?? 0, total));
    const clampedEnd = Math.max(0, Math.min(end ?? 0, total));

    if (clampedStart >= clampedEnd) return null;

    let offset = 0;
    let fromPoint = null;
    let toPoint = null;

    for (const textNode of textNodes) {
        const len = textNode.nodeValue.length;
        const next = offset + len;

        if (!fromPoint && clampedStart >= offset && clampedStart <= next) {
            fromPoint = {node: textNode, offset: clampedStart - offset};
        }
        if (!toPoint && clampedEnd >= offset && clampedEnd <= next) {
            toPoint = {node: textNode, offset: clampedEnd - offset};
        }

        if (fromPoint && toPoint) break;
        offset = next;
    }

    if (!fromPoint || !toPoint) return null;

    return {fromPoint, toPoint};
}

/**
 * Convert a (blockId, [start,end]) range into ProseMirror doc positions.
 *
 * Assumes `start/end` are character offsets within the rendered plain text
 * of that block (in DOM text-node order).
 *
 * @param {import('prosemirror-view').EditorView} view
 * @param {string} blockId
 * @param {number} start
 * @param {number} end
 * @returns {DocRange | null}
 */
export function blockRangeToDocPositions(view, blockId, start, end) {
    if (!view || !blockId) return null;

    const blockEl = view.dom.querySelector(`[data-block-id="${blockId}"]`);
    if (!blockEl) {
        console.warn(`blockRangeToDocPositions: No element found for blockId=${blockId}`);
        return blockTextOffsetsToDocPositions(view.state.doc, blockId, start, end);
    }

    const boundaries = findDomTextBoundaries(blockEl, start, end);
    if (!boundaries) {
        return blockTextOffsetsToDocPositions(view.state.doc, blockId, start, end);
    }

    const {fromPoint, toPoint} = boundaries;

    try {
        const from = view.posAtDOM(fromPoint.node, fromPoint.offset);
        const to = view.posAtDOM(toPoint.node, toPoint.offset);

        if (typeof from !== 'number' || typeof to !== 'number') {
            return blockTextOffsetsToDocPositions(view.state.doc, blockId, start, end);
        }

        return {
            from: Math.min(from, to),
            to: Math.max(from, to),
        };
    } catch (e) {
        console.warn('blockRangeToDocPositions: posAtDOM failed', e);
        return blockTextOffsetsToDocPositions(view.state.doc, blockId, start, end);
    }
}
